<app-sheet [open]="open" [deskW]="880" maxH="94%" [pad]="false" (close)="close()">
  <!-- loading item details -->
  <div *ngIf="loading && !item" style="flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;min-height:300px;padding:48px">
    <app-spinner [size]="30" color="var(--brand)"></app-spinner>
    <span style="font-size:13px;color:var(--fr-text-3)">{{ 'Loading…' | t }}</span>
  </div>

  <ng-container *ngIf="item as it">
    <!-- ═══════════════ Desktop (≥900px): photo | details + options + footer ═══════════════ -->
    <div *ngIf="vp.isDesktop$ | async; else mobileItem" class="dk-item">
      <!-- the photo keeps its shape (square, cropped — never stretched); a blurred copy of it
           fills the rest of the column however tall the modal grows -->
      <div class="dk-item-media">
        <img *ngIf="photo" [src]="photo" alt="" class="dk-item-backdrop" aria-hidden="true" onerror="this.style.display='none'">
        <app-img-slot [src]="photo" [tone]="it.tone" [radius]="0" fit="cover" [scrim]="false" class="dk-item-photo"></app-img-slot>
        <div class="dk-item-tags">
          <app-tag *ngIf="it.tags.includes('best')" kind="best"></app-tag>
          <app-tag *ngIf="it.tags.includes('spicy')" kind="spicy"></app-tag>
        </div>
      </div>

      <div class="dk-item-main">
        <!-- header -->
        <div class="dk-item-head">
          <div style="flex:1;min-width:0">
            <h2>{{ it.name }}</h2>
            <p *ngIf="it.desc">{{ it.desc }}</p>
            <div class="meta">
              <app-price [value]="headerPrice" [size]="17" [weight]="800"></app-price>
              <span *ngIf="kcal" class="fr-num kcal">{{ kcal }} kcal</span>
              <button *ngIf="it.allergens.length" class="allerg" (click)="allergensOpen = !allergensOpen">
                {{ 'Allergens' | t }} <app-icon [name]="allergensOpen ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'" [size]="13"></app-icon>
              </button>
            </div>
            <div *ngIf="allergensOpen && it.allergens.length" class="chips">
              <span *ngFor="let a of it.allergens">{{ a | t }}</span>
            </div>
          </div>
          <button class="dk-item-x" (click)="close()" [attr.aria-label]="'Close' | t"><app-icon name="solar:close-circle-linear" [size]="19" color="var(--fr-text-2)"></app-icon></button>
        </div>

        <!-- body -->
        <div #scroll class="dk-item-body">
          <!-- weight items: pick a fraction of a kg -->
          <div *ngIf="isWeight" class="dk-grp">
            <div class="dk-grp-head">
              <div style="flex:1"><div class="n">{{ 'Quantity' | t }}</div></div>
              <span class="fr-num" style="font-weight:800;font-size:16px;color:var(--brand-deep)">{{ weightLabel }}</span>
            </div>
            <div class="dk-weights">
              <button *ngFor="let w of weightSteps" class="fr-num" [class.on]="qty === w.v" (click)="setWeight(w.v)">{{ w.label }}</button>
            </div>
          </div>

          <div *ngFor="let g of groups" [attr.data-gid]="g.id" class="dk-grp">
            <div class="dk-grp-head">
              <div style="flex:1">
                <div class="n">{{ g.name | t }}</div>
                <div class="sub" [class.bad]="groupIncomplete(g)">{{ groupSub(g) | t }}<ng-container *ngIf="g.qty"> · {{ 'add quantities' | t }}</ng-container></div>
              </div>
              <span class="badge" [class.req]="g.required" [class.bad]="groupIncomplete(g)">{{ (g.required ? 'Required' : 'Optional') | t }}</span>
            </div>
            <div class="dk-opts" [class.one]="g.qty">
              <div *ngFor="let o of g.options" class="dk-opt" [class.on]="isActive(g, o) && !g.qty" [class.qty]="g.qty" [class.off]="o.soldOut" [attr.aria-disabled]="o.soldOut || null" (click)="onOptionClick(g, o)">
                <span *ngIf="!g.qty" class="mark" [class.round]="g.type === 'single'"><app-icon *ngIf="isActive(g, o)" name="solar:check-read-linear" [size]="13" color="#fff"></app-icon></span>
                <div style="flex:1;min-width:0">
                  <div class="nm">{{ o.name | t }}</div>
                  <div *ngIf="o.note" class="note">{{ o.note | t }}</div>
                </div>
                <span *ngIf="o.popular && !o.soldOut" class="pop">{{ 'Popular' | t }}</span>
                <span *ngIf="o.soldOut" class="sold">{{ 'Sold out' | t }}</span>
                <!-- variants show their full price (no "+"); add-ons/modifiers show the delta -->
                <app-price *ngIf="g.apiKind === 'variant' && !o.soldOut" [value]="it.price + o.price" [size]="13"></app-price>
                <span *ngIf="g.apiKind !== 'variant' && o.price > 0 && !o.soldOut" class="fr-num delta">+{{ o.price }}</span>
                <div *ngIf="g.qty && !o.soldOut" (click)="$event.stopPropagation()">
                  <app-stepper size="sm" [value]="optCount(g, o)" [min]="0" [max]="9" (valueChange)="setQtyOpt(g, o, $event)"></app-stepper>
                </div>
              </div>
            </div>
          </div>

          <div class="dk-grp last">
            <div class="n" style="margin-bottom:8px">{{ 'Special instructions' | t }}</div>
            <textarea [(ngModel)]="note" [placeholder]="'e.g. extra crispy, sauce on the side' | t" rows="2" maxlength="200" class="dk-item-note"></textarea>
          </div>
        </div>

        <!-- footer -->
        <div class="dk-item-foot">
          <app-stepper *ngIf="!isWeight" [value]="qty" [min]="1" [max]="20" (valueChange)="qty = $event"></app-stepper>
          <div *ngIf="isWeight" class="fr-num" style="flex-shrink:0;font-weight:800;font-size:16px;color:var(--fr-text);white-space:nowrap;padding:0 4px">{{ weightLabel }}</div>
          <button class="dk-item-add" [class.dim]="tried && !valid" [disabled]="addBlocked" (click)="tryAdd()">
            <span>{{ ctaLabel | t }}</span>
            <ng-container *ngIf="!soldOut">
              <span style="opacity:.55">·</span>
              <app-price [value]="total" [size]="16" [weight]="750" color="#fff"></app-price>
            </ng-container>
          </button>
        </div>
      </div>
    </div>

    <!-- ═══════════════ Phones ═══════════════ -->
    <ng-template #mobileItem>
      <!-- hero: pinned to the top of the sheet so the photo stays in view while the options scroll -->
      <app-img-slot [src]="photo" [tone]="it.tone" [radius]="0" fit="cover" class="it-hero">
        <button (click)="close()" style="position:absolute;top:12px;inset-inline-end:12px;width:36px;height:36px;border-radius:50%;border:none;background:rgba(0,0,0,0.4);color:#fff;cursor:pointer;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);z-index:3">
          <app-icon name="solar:close-linear" [size]="20"></app-icon>
        </button>
        <div style="position:absolute;bottom:12px;inset-inline-start:14px;display:flex;gap:6px">
          <app-tag *ngIf="it.tags.includes('best')" kind="best"></app-tag>
          <app-tag *ngIf="it.tags.includes('spicy')" kind="spicy"></app-tag>
        </div>
      </app-img-slot>

    <div #scroll class="it-mscroll">

      <div style="padding:18px 18px 8px">
        <h2 style="margin:0;font-family:var(--brand-display);font-weight:700;font-size:24px;letter-spacing:-0.02em;color:var(--fr-text)">{{ it.name }}</h2>
        <p style="margin:8px 0 0;font-size:14px;line-height:1.5;color:var(--fr-text-2)">{{ it.desc }}</p>
        <div style="display:flex;align-items:center;gap:14px;margin-top:12px">
          <app-price [value]="headerPrice" [size]="18"></app-price>
          <span *ngIf="kcal" class="fr-num" style="display:inline-flex;align-items:center;gap:4px;font-size:12.5px;color:var(--fr-text-3)">
            <app-icon name="solar:fire-linear" [size]="14"></app-icon> {{ kcal }} kcal</span>
          <button *ngIf="it.allergens.length" (click)="allergensOpen = !allergensOpen"
            [style.background]="allergensOpen ? 'var(--brand-tint)' : 'var(--fr-surface-3)'"
            [style.color]="allergensOpen ? 'var(--brand-deep)' : 'var(--fr-text-2)'"
            style="margin-inline-start:auto;display:inline-flex;align-items:center;gap:4px;border:none;cursor:pointer;padding:6px 10px;border-radius:9px;font-size:12px;font-weight:700;font-family:var(--fr-font)">
            <app-icon name="solar:danger-circle-linear" [size]="14"></app-icon> {{ 'Allergens' | t }}
            <app-icon [name]="allergensOpen ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'" [size]="13"></app-icon>
          </button>
        </div>
        <div *ngIf="allergensOpen && it.allergens.length" style="margin-top:10px;padding:10px 12px;background:var(--fr-surface-3);border-radius:12px">
          <div style="display:flex;flex-wrap:wrap;gap:6px">
            <span *ngFor="let a of it.allergens" style="display:inline-flex;align-items:center;gap:4px;background:var(--fr-surface);border:1px solid var(--fr-border-2);border-radius:999px;padding:4px 10px;font-size:11.5px;font-weight:650;color:var(--fr-text-2)">{{ a | t }}</span>
          </div>
        </div>
      </div>

      <!-- weight items: sold by the kilo — pick a fraction of a kg -->
   

      <!-- modifier groups -->
      <div *ngFor="let g of groups" [attr.data-gid]="g.id" style="margin-top:14px;padding-top:14px;border-top:8px solid var(--fr-surface-3)">
        <div style="display:flex;align-items:center;justify-content:space-between;padding:0 18px 10px">
          <div>
            <div style="font-weight:750;font-size:16px;letter-spacing:-0.01em;color:var(--fr-text)">{{ g.name | t }}</div>
            <div [style.color]="groupIncomplete(g) ? 'var(--fr-danger)' : 'var(--fr-text-3)'" style="font-size:12.5px;margin-top:2px">{{ groupSub(g) | t }}<ng-container *ngIf="g.qty"> · {{ 'add quantities' | t }}</ng-container></div>
          </div>
          <span [style.color]="g.required ? (groupIncomplete(g) ? '#fff' : 'var(--brand-deep)') : 'var(--fr-text-3)'"
                [style.background]="g.required ? (groupIncomplete(g) ? 'var(--fr-danger)' : 'var(--brand-tint)') : 'var(--fr-surface-3)'"
                style="font-size:11px;font-weight:750;padding:4px 9px;border-radius:7px">{{ (g.required ? 'Required' : 'Optional') | t }}</span>
        </div>
        <div style="padding:0 14px">
          <div *ngFor="let o of g.options" (click)="onOptionClick(g, o)"
            [attr.aria-disabled]="o.soldOut || null" [style.opacity]="o.soldOut ? 0.45 : 1"
            [style.cursor]="g.qty || o.soldOut ? 'default' : 'pointer'"
            [style.background]="isActive(g, o) && !g.qty ? 'var(--brand-tint)' : 'transparent'"
            style="display:flex;align-items:center;gap:12px;padding:11px 8px;border-radius:12px">
            <span *ngIf="!g.qty" [style.border-radius]="g.type === 'single' ? '50%' : '7px'"
              [style.background]="isActive(g, o) ? 'var(--brand)' : 'var(--fr-surface)'"
              [style.box-shadow]="isActive(g, o) ? 'none' : 'inset 0 0 0 2px var(--fr-border-2)'"
              style="width:22px;height:22px;flex-shrink:0;display:flex;align-items:center;justify-content:center">
              <app-icon *ngIf="isActive(g, o)" name="solar:check-read-linear" [size]="14" color="#fff"></app-icon>
            </span>
            <div style="flex:1;min-width:0">
              <div style="display:flex;align-items:center;gap:7px">
                <span style="font-weight:650;font-size:14.5px;color:var(--fr-text)">{{ o.name | t }}</span>
                <span *ngIf="o.popular && !o.soldOut" style="font-size:10.5px;font-weight:750;color:var(--gold-deep);background:var(--gold-tint);padding:2px 6px;border-radius:5px">{{ 'Popular' | t }}</span>
              </div>
              <div *ngIf="o.note" style="font-size:12px;color:var(--fr-text-3);margin-top:1px">{{ o.note | t }}</div>
            </div>
            <!-- variants show their full price (no "+"); add-ons/modifiers show the delta -->
            <span *ngIf="o.soldOut" style="font-size:11px;font-weight:750;color:var(--fr-danger);background:var(--fr-surface-3);padding:3px 8px;border-radius:6px;white-space:nowrap">{{ 'Sold out' | t }}</span>
            <app-price *ngIf="g.apiKind === 'variant' && !o.soldOut" [value]="item.price + o.price" [size]="13"></app-price>
            <span *ngIf="g.apiKind !== 'variant' && o.price > 0 && !o.soldOut" class="fr-num" style="font-size:13px;font-weight:650;color:var(--fr-text-2)">+{{ o.price }}</span>
            <div *ngIf="g.qty && !o.soldOut" style="margin-inline-start:4px" (click)="$event.stopPropagation()">
              <app-stepper size="sm" [value]="optCount(g, o)" [min]="0" [max]="9" (valueChange)="setQtyOpt(g, o, $event)"></app-stepper>
            </div>
          </div>
        </div>
      </div>

      <!-- special instructions -->
      <div style="margin-top:14px;padding-top:16px;border-top:8px solid var(--fr-surface-3)">
        <div style="padding:0 18px">
          <div style="font-weight:750;font-size:16px;margin-bottom:10px;color:var(--fr-text)">{{ 'Special instructions' | t }}</div>
          <textarea [(ngModel)]="note" [placeholder]="'e.g. extra crispy, sauce on the side' | t" rows="2" maxlength="200"
            style="width:100%;resize:none;border:1.5px solid var(--fr-border-2);border-radius:12px;padding:11px 13px;font-size:14px;font-family:var(--fr-font);outline:none;color:var(--fr-text);background:var(--fr-surface)"></textarea>
          <div style="text-align:end;font-size:11.5px;color:var(--fr-text-3);margin-top:5px"><span class="fr-num">{{ 200 - note.length }}</span> {{ 'characters left' | t }}</div>
        </div>
      </div>
      <!-- <div style="height:90px"></div> -->
    </div>
   <div *ngIf="isWeight" style="margin-top:14px;padding-top:14px;border-top:8px solid var(--fr-surface-3)">
        <div style="display:flex;align-items:baseline;justify-content:space-between;padding:0 18px">
          <div style="font-weight:750;font-size:15px;color:var(--fr-text)">{{ 'Quantity' | t }}</div>
          <div class="fr-num" style="font-weight:800;font-size:16px;color:var(--brand-deep)">{{ weightLabel }}</div>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:8px;padding:12px 18px 0">
          <button *ngFor="let w of weightSteps" (click)="setWeight(w.v)"
            [style.border]="'1.5px solid ' + (qty === w.v ? 'var(--brand)' : 'var(--fr-border-2)')"
            [style.background]="qty === w.v ? 'var(--brand-tint)' : 'var(--fr-surface)'"
            [style.color]="qty === w.v ? 'var(--brand-deep)' : 'var(--fr-text)'"
            class="fr-num" style="min-width:56px;border-radius:11px;padding:10px 12px;font-family:var(--fr-font);font-weight:750;font-size:14px;cursor:pointer">
            {{ w.label }}
          </button>
        </div>
      </div>
    <!-- sticky footer -->
    <div sheetFooter style="flex-shrink:0;border-top:1px solid var(--fr-border);background:var(--fr-surface);padding:12px 16px calc(12px + env(safe-area-inset-bottom));display:flex;align-items:center;gap:14px">
      <app-stepper *ngIf="!isWeight" [value]="qty" [min]="1" [max]="20" (valueChange)="qty = $event"></app-stepper>
      <div *ngIf="isWeight" class="fr-num" style="flex-shrink:0;font-weight:800;font-size:16px;color:var(--fr-text);white-space:nowrap;padding:0 4px">{{ weightLabel }}</div>
      <app-btn [full]="true" size="lg" [disabled]="addBlocked" (act)="tryAdd()" [style.flex]="1" style="flex:1">
        <span style="display:flex;align-items:center;justify-content:space-between;width:100%" [style.justify-content]="soldOut ? 'center' : null">
          <span style="display:flex;align-items:center;gap:8px">
            <app-icon [name]="soldOut ? 'solar:forbidden-circle-bold' : 'solar:bag-smile-bold'" [size]="19"></app-icon>
            {{ ctaLabel | t }}
          </span>
          <app-price *ngIf="!soldOut" [value]="total" [size]="16" [weight]="750" color="#fff"></app-price>
        </span>
      </app-btn>
    </div>
    </ng-template>
  </ng-container>
</app-sheet>
